import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import handleError from "go-app/services/errors";
import MessageEventModal from "go-component/components/MessageEvent/MessageEventModal";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { LoadingContainer } from "go-core/components/Loading";
import RouteHistory from "go-core/utils/routeHistory";
import { useConfirmation } from "go-form/components/ModalConfirm";
import AccessTokenSessionService from "go-security/services/accessTokenSessionService";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { selectIsAdmin } from "go-security/services/users/selectors";
import AppFormatResourceStatusLabel from "../../../../../../../../components/FormatStatus";
import { ApplicationApi } from "../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../services/Api/api";
import AppErrorMessage from "../../components/AppErrorMessage";
import DocumentFileImportForm from "../../components/Apps/DocumentFileImport/DocumentFileImportForm";
import DocumentTransferForm from "../../components/Apps/DocumentTransfer/DocumentTransferForm";
import GastroSupplierForm from "../../components/Apps/GastroSupplier/GastroSupplierForm";
import GastroSupplierMerchantForm from "../../components/Apps/GastroSupplierMerchant/GastroSupplierMerchantForm";
import GoPosForm from "../../components/Apps/GoPos/GoPosForm";
import MultilocationForm from "../../components/Apps/MultiLocation/MultilocationForm";
import { Providers } from "../../services/types";

interface Props {
	handleUninstall: (app: ApplicationApi) => void;
	handleUpdate: (app: ApplicationApi) => void;
	setCurrentAppId: (appId: number | null) => void;
}

interface MatchParams {
	app_id: string;
}

export const OrganizationAppsEditPage: FC<Props> = ({ handleUninstall, handleUpdate, setCurrentAppId }) => {
	const history = useHistory();
	const { addFlash, addSuccessFlash } = useFlash();
	const { t } = useTranslation();
	const { app_id } = useParams<MatchParams>();
	const organization = useSelector(selectOrganization);
	const location = useLocation();
	const params: Record<string, any> = { include: "settings" };
	const [config, setConfig] = useState<Record<string, any> | undefined>();
	const isAdmin = useSelector(selectIsAdmin);
	const [showLogs, setShowLogs] = useState(false);
	const confirmation = useConfirmation();
	const [app, setApp] = useState<ApplicationApi | undefined>();
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		getApp();

		return () => setCurrentAppId(null);
	}, []);

	useEffect(() => {
		setCurrentAppId(Number(app_id));
	}, [app_id]);

	const getApp = async () => {
		try {
			const res = await Promise.all([
				api.organization().getApp(Number(app_id), { include: "settings" }),
				api.organization().getAppsConfig(Number(app_id)),
			]);
			setApp(res[0]);
			setConfig(res[1]);
			handleChangeTabTitle(
				`${res[0].name} | ${
					location.pathname.includes("active")
						? t("modules.app.field.active.title")
						: t("modules.app.field.all.title")
				} `
			);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const fetchConfig = async () => {
		try {
			const res = await api.organization().getAppsConfig(Number(app_id));
			setConfig(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	if (!config || !app) {
		return <LoadingContainer />;
	}

	const handleActivateApp = async () => {
		try {
			if (app) {
				const res = await api.organization().enableApp(app, params);
				await fetchConfig();
				setApp(res);
				addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
				if (handleUpdate) {
					handleUpdate(res);
				}
			}
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const handleDeactivateApp = async () => {
		try {
			if (app) {
				const res = await api.organization().deactivateApp(app, params);
				await fetchConfig();
				setApp(res);
				addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
				if (handleUpdate) {
					handleUpdate(res);
				}
			}
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const handleResetApp = async () => {
		try {
			if (app) {
				const res = await api.organization().resetApp(app, params);
				setApp(res);
				await fetchConfig();
				addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
				if (handleUpdate) {
					handleUpdate(res);
				}
			}
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const handleSyncApp = async () => {
		try {
			if (app) {
				await api.organization().syncApp(app);
				addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
			}
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const handleUninstallApp = async () => {
		try {
			if (app) {
				await confirmation({
					title: t("confirmation.title", { ns: "lib" }),
					message: t("confirmation.message.remove", { ns: "lib" }),
				});
				await api.organization().removeApp(app);
				addSuccessFlash(t("common.flash.removed", { ns: "lib" }));
				if (handleUninstall) {
					handleUninstall(app);
				}
			}
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const handleReinstallChannel = () => {
		const accessToken = AccessTokenSessionService.get();

		if (app.provider === Providers.GOPOS || app.provider === Providers.GOPOS_V2) {
			return api.organization().reinstallApplication(app, accessToken ? accessToken : "");
		}

		handleActivateApp();
	};

	const enableApplication = async () => {
		try {
			const enableParams: Record<string, any> = { include: "settings" };
			const enabledApp = await api.organization().enableApp(app, enableParams);
			addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
			const newConfig = await api.organization().getAppsConfig(enabledApp.id);
			setApp(enabledApp);
			setConfig({ config: newConfig, app: enabledApp });
			if (handleUpdate) {
				handleUpdate(enabledApp);
			}
		} catch (e) {
			handleError.alert(e, addFlash);
		}
	};

	const fixErrorHandler = async () => {
		try {
			const accessToken = AccessTokenSessionService.get();
			if (app?.provider === Providers.GOPOS || app?.provider === Providers.GOPOS_V2) {
				api.organization().reinstallApplication(app, accessToken ?? "");
			} else {
				await enableApplication();
			}
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const renderTitle = () => (
		<>
			{`${t("common.word.edition")} "${app?.name}"`}
			{app?.status && (
				<AppFormatResourceStatusLabel status={app?.status === "NEW" ? "INACTIVE_APP" : app?.status} />
			)}
		</>
	);

	const backPath = location.pathname.includes("active")
		? `/${organization.id}/apps/active`
		: `/${organization.id}/apps/all`;
	const goBack = () => RouteHistory.goBack(history, backPath);

	const buttons: ButtonProps[] = [
		{
			title: t("modules.app.action.reinstall_channel.title"),
			action: handleReinstallChannel,
			dropdown: true,
		},
	];
	if (app?.status !== "ENABLED" && app?.status !== "ERROR") {
		buttons.push({
			title: t("lib:common.action.activate"),
			action: handleActivateApp,
			variant: "primary",
		});
	}
	if (app?.status === "ENABLED") {
		buttons.push({
			title: t("lib:common.action.disable"),
			action: handleDeactivateApp,
			dropdown: true,
		});
	}
	if (app?.status !== "DELETED" && (isAdmin || organization.support)) {
		buttons.push({
			title: t("lib:common.action.reset"),
			action: handleResetApp,
			dropdown: true,
		});
	}
	if (app?.status !== "DELETED" && app?.status !== "ERROR") {
		buttons.push({
			title: t("lib:common.action.sync"),
			action: handleSyncApp,
			variant: "primary",
		});
	}
	if (app?.status === "ENABLED") {
		buttons.push({
			title: t("modules.app.action.uninstall.title"),
			action: handleUninstallApp,
			dropdown: true,
		});
	}
	if (app?.status === "ERROR" && app.provider === Providers.LOCATIONS)
		buttons.push({
			title: t("common.action.fix_error", { ns: "lib" }),
			dropdown: true,
			action: handleActivateApp,
		});

	buttons.push({
		title: t("lib:common.word.logs"),
		dropdown: true,
		action: () => setShowLogs(true),
	});

	if (app?.status === "ERROR" && app.provider !== Providers.DOCUMENT_FILE_IMPORT) {
		buttons.push({
			title: t("common.action.fix_error", { ns: "lib" }),
			action: fixErrorHandler,
			variant: "danger",
		});
	}

	const drawForm = () => {
		switch (app?.provider) {
			case Providers.GOPOS:
			case Providers.GOPOS_V2:
				return <GoPosForm appState={{ app, config }} handleUpdateApp={handleUpdate} />;
			case Providers.LOCATIONS:
				return <MultilocationForm appState={{ app, config }} handleUpdateApp={handleUpdate} />;
			case Providers.GASTROSUPPLIER:
				return <GastroSupplierForm appState={{ app, config }} handleUpdateApp={handleUpdate} />;
			case Providers.DOCUMENT_TRANSFER:
				return <DocumentTransferForm appState={{ app, config }} handleUpdateApp={handleUpdate} />;
			case Providers.GASTROSUPPLIER_MERCHANT:
				return <GastroSupplierMerchantForm appState={{ app, config }} handleUpdateApp={handleUpdate} />;
			case Providers.DOCUMENT_FILE_IMPORT:
				return <DocumentFileImportForm appState={{ app, config }} handleUpdateApp={handleUpdate} />;
			default:
				return <></>;
		}
	};

	return (
		<>
			<Header title={renderTitle()} back={goBack} buttons={buttons} />
			{app.errors.length > 0 && <AppErrorMessage message={app.errors[0].message} />}
			{drawForm()}
			{showLogs && (
				<MessageEventModal
					path={`/${organization.id}/logs/message_events`}
					resourceId={app?.id || ""}
					resourceType="INTEGRATION_CHANNEL"
					onHide={() => setShowLogs(!showLogs)}
					organizationId={organization.id}
				/>
			)}
		</>
	);
};
